import { g, layers, LayerClass, Tool } from '@hcie/core';
import { LayerAddAction, LayerDeleteAction, LayerMoveAction, LayerPropertyAction } from '@hcie/core';
import { drawShapesToCtx, Shape, EventBus } from '@hcie/shared';

import { drawSelectionBorder } from '@hcie/tools';

const ctx = (document.getElementById("drawingCanvas") as HTMLCanvasElement)?.getContext("2d");
const originalCanvas = document.getElementById('originalCanvas') as HTMLCanvasElement | null;

/**
 * Layer Manager
 * Handles layer operations: add, delete, select, visibility, reorder, render
 */

// ─── Layer Operations ─────────────────────────────────────

/**
 * Add a new layer above the active layer
 */
export function addLayer(name?: string) {
    const idx = g.activeLayerIndex + 1;
    const layerName = name || `Layer ${layers.length}`;
    const newLayer = new LayerClass(layerName, g.image_width, g.image_height);

    // Check if we want a vector layer (simple check for now, can be expanded)
    if (name && name.toLowerCase().includes('vector')) {
        newLayer.type = 'vector';
        newLayer.shapes = []; // Array to hold shape objects
    }

    layers.splice(idx, 0, newLayer);
    g.activeLayerIndex = idx;

    if (window.historyManager) {
        window.historyManager.push(new LayerAddAction(idx, newLayer));
    }

    renderLayers();
    updateLayerPanel();
    console.log(`Added layer "${layerName}" at index ${idx}`);
}

export function addVectorLayer() {
    addLayer("Vector Layer");
}

/**
 * Delete the active layer (prevents deleting the last layer)
 */
export function deleteLayer() {
    if (layers.length <= 1) {
        console.warn("Cannot delete the last layer");
        return;
    }
    const layerToDelete = layers[g.activeLayerIndex];
    const index = g.activeLayerIndex;

    const removed = layers.splice(g.activeLayerIndex, 1);

    if (window.historyManager) {
        window.historyManager.push(new LayerDeleteAction(index, layerToDelete));
    }

    console.log(`Deleted layer "${removed[0].name}"`);
    // Adjust active index
    if (g.activeLayerIndex >= layers.length) {
        g.activeLayerIndex = layers.length - 1;
    }
    renderLayers();
    updateLayerPanel();
}

/**
 * Select a layer by index
 */
export function selectLayer(index: number) {
    if (index < 0 || index >= layers.length) {
        console.error(`Layer index ${index} out of range`);
        return;
    }
    g.activeLayerIndex = index;
    updateLayerPanel();
    console.log(`Selected layer "${layers[index].name}" (index ${index})`);
}

/**
 * Toggle visibility of a layer
 */
export function toggleLayerVisibility(index: number) {
    if (index < 0 || index >= layers.length) return;

    const oldValue = layers[index].visible;
    layers[index].visible = !layers[index].visible;
    const newValue = layers[index].visible;

    if (window.historyManager) {
        window.historyManager.push(new LayerPropertyAction(index, 'visible', oldValue, newValue));
    }

    renderLayers();
    updateLayerPanel();
}

/**
 * Move a layer from one position to another
 */
export function moveLayer(fromIndex: number, toIndex: number, skipRecord = false) {
    if (fromIndex < 0 || fromIndex >= layers.length) return;
    if (toIndex < 0 || toIndex >= layers.length) return;

    if (!skipRecord && window.historyManager) {
        window.historyManager.push(new LayerMoveAction(fromIndex, toIndex));
    }

    const [layer] = layers.splice(fromIndex, 1);
    layers.splice(toIndex, 0, layer);

    // Update active index to follow the moved layer
    if (g.activeLayerIndex === fromIndex) {
        g.activeLayerIndex = toIndex;
    } else if (fromIndex < g.activeLayerIndex && toIndex >= g.activeLayerIndex) {
        g.activeLayerIndex--;
    } else if (fromIndex > g.activeLayerIndex && toIndex <= g.activeLayerIndex) {
        g.activeLayerIndex++;
    }
    renderLayers();
    updateLayerPanel();
}

/**
 * Move active layer up (visually higher = later in array)
 */
export function moveLayerUp() {
    if (g.activeLayerIndex < layers.length - 1) {
        moveLayer(g.activeLayerIndex, g.activeLayerIndex + 1);
    }
}

/**
 * Move active layer down (visually lower = earlier in array)
 */
export function moveLayerDown() {
    if (g.activeLayerIndex > 0) {
        moveLayer(g.activeLayerIndex, g.activeLayerIndex - 1);
    }
}

/**
 * Rename a layer
 */
export function renameLayer(index: number, newName: string) {
    if (index < 0 || index >= layers.length) return;

    const oldName = layers[index].name;
    layers[index].name = newName;

    if (window.historyManager && oldName !== newName) {
        window.historyManager.push(new LayerPropertyAction(index, 'name', oldName, newName));
    }

    updateLayerPanel();
}


// ─── Rendering ─────────────────────────────────────────────

/**
 * Composite all visible layers onto the drawing canvas (bottom to top)
 * @param {HTMLCanvasElement} [liveCanvas] - Optional temp canvas for the active layer (used during drawing)
 */
export function renderLayers(liveCanvas?: HTMLCanvasElement) {
    if (g.drawing && Date.now() % 500 < 20) {
        console.log(`[DEBUG 2026-04-05][TaskID: #1324] renderLayers: liveCanvas=${!!liveCanvas}, g.drawing=${g.drawing}`);
    }
    const canvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Use tempCanvas if liveCanvas not provided but drawing is active
    if (!liveCanvas && g.drawing) {
        liveCanvas = (window as any).tempCanvas || document.getElementById("tempCanvas") as HTMLCanvasElement;
        if (liveCanvas && Date.now() % 500 < 20) {
            console.log(`[DEBUG 2026-04-05][TaskID: #1324] renderLayers: Fallback to tempCanvas found=${!!liveCanvas}`);
        }
    }

    // Clear the display canvas
    ctx.clearRect(0, 0, g.image_width, g.image_height);

    // Composite each visible layer
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        if (!layer.visible) continue;

        ctx.save();
        ctx.globalAlpha = layer.opacity;
        ctx.globalCompositeOperation = layer.blendMode;

        // Draw the permanent layer content (raster pixels)
        ctx.drawImage(layer.canvas as HTMLCanvasElement, 0, 0);

        // Render shapes for THIS layer (if any exist)
        // If a shape is currently being edited (ghost edit), skip the original shape
        // so we don't draw both old+new at the same time.
        if (layer.shapes && layer.shapes.length > 0) {
            const vtm = (window as any).vectorToolManager;
            const target = (window as any).vectorEditingTarget;
            const isEditingThisLayer =
                vtm?.isEditing &&
                target &&
                typeof target.layerIndex === 'number' &&
                target.layerIndex === i &&
                typeof target.shapeIndex === 'number' &&
                target.shapeIndex >= 0 &&
                target.shapeIndex < layer.shapes.length;
            if (isEditingThisLayer) {
                const shapes = layer.shapes.filter((_s: any, idx: number) => idx !== target.shapeIndex);
                drawShapesToCtx(ctx, shapes);
            } else {
                drawShapesToCtx(ctx, layer.shapes);
            }
        }

        // ✨ AI ARCHITECTURE NOTE: Live Vector Drawing Preview
        // We draw the 'in-progress' vector shape directly from the manager to avoid rasterizing
        // every frame into tempCanvas. This prevents 'ghosting' artifacts.
        const vtm = (window as any).vectorToolManager;
        const isVtmActive = vtm && (vtm.isDrawing || vtm.isEditing) && vtm.currentShape;
        if (isVtmActive && i === g.activeLayerIndex) {
            ctx.save();
            // If editing, maybe show it slightly differently or with higher precedence
            drawShapesToCtx(ctx, [vtm.currentShape]);
            ctx.restore();
        }

        // If this is the active layer and we are drawing (liveCanvas exists),
        // draw the temporary stroke (composition happens after selection masking)
        if (liveCanvas && i === g.activeLayerIndex) {
            const isEraser = g.current_tool.id === Tool.Eraser.id;
            
            // Apply selection mask to the live preview if active
            if (g.isSelectionActive && g.selectionCanvas) {
                // Persistent buffer to avoid allocation overhead during mousemove
                if (!(window as any)._maskedStrokeBuffer) {
                    (window as any)._maskedStrokeBuffer = g.createCanvas(g.image_width, g.image_height);
                }
                const offCanvas = (window as any)._maskedStrokeBuffer;
                if (offCanvas.width !== g.image_width || offCanvas.height !== g.image_height) {
                    offCanvas.width = g.image_width;
                    offCanvas.height = g.image_height;
                }
                
                const offCtx = offCanvas.getContext("2d");
                if (offCtx) {
                    offCtx.clearRect(0, 0, g.image_width, g.image_height);
                    
                    // Copy stroke to buffer
                    offCtx.globalAlpha = 1.0;
                    offCtx.globalCompositeOperation = "source-over";
                    offCtx.drawImage(liveCanvas, 0, 0);
                    
                    // Apply mask
                    offCtx.globalCompositeOperation = "destination-in";
                    offCtx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
                    
                    // Draw masked result (inherits layer's alpha/blend from current ctx state)
                    if (isEraser) ctx.globalCompositeOperation = "destination-out";
                    ctx.drawImage(offCanvas, 0, 0);
                }
            } else {
                if (isEraser) ctx.globalCompositeOperation = "destination-out";
                ctx.drawImage(liveCanvas, 0, 0);
            }
        }

        ctx.restore();
    }

    // Draw Floating Selection Content (Move Content tool)
    if (g.floatingContent && g.floatingContent.canvas) {
        ctx.save();
        ctx.drawImage(g.floatingContent.canvas as HTMLCanvasElement, g.floatingContent.x, g.floatingContent.y);
        ctx.restore();
    }

    // Draw Selection Border (Marching Ants) if active
    drawSelectionBorder(ctx);

    // Draw Ghost Selection Handles (shape selection edit mode)
    if ((g as any).settings?.selection?.ghostMode && g.isSelectionActive && (g as any)._selectionEditBounds) {
        const b = (g as any)._selectionEditBounds;
        const x1 = b.x1, y1 = b.y1, x2 = b.x2, y2 = b.y2;
        const s = 10 / (g.zoom || 1); // keep roughly constant screen size
        const handles = [
            { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
            { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
            { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 },
        ];

        ctx.save();
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#00bcd4";
        ctx.lineWidth = Math.max(1, 1 / (g.zoom || 1));
        for (const h of handles) {
            ctx.beginPath();
            ctx.rect(h.x - s / 2, h.y - s / 2, s, s);
            ctx.fill();
            ctx.stroke();
        }
        ctx.restore();
    }

    // Draw Crop UI if a crop area is pending confirmation
    if ((g as any).lastCropRect) {
        const r = (g as any).lastCropRect;
        // Avoid drawing a full-screen blackout if dimensions are too small (threshold 5px)
        if (r.w > 5 && r.h > 5) {
            ctx.save();
        
        // 1. Draw a semi-transparent dark overlay outside the crop area
        ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
        // Top
        ctx.fillRect(0, 0, g.image_width, r.y);
        // Bottom
        ctx.fillRect(0, r.y + r.h, g.image_width, g.image_height - (r.y + r.h));
        // Left
        ctx.fillRect(0, r.y, r.x, r.h);
        // Right
        ctx.fillRect(r.x + r.w, r.y, g.image_width - (r.x + r.w), r.h);

        // 2. Draw solid blue border for crop boundary
        ctx.strokeStyle = "rgba(0, 120, 215, 1.0)";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(r.x, r.y, r.w, r.h);

        // 3. Draw handles at corners and midpoints
        const s = 7; // handle size
        const handles = [
            {x: r.x, y: r.y}, {x: r.x + r.w/2, y: r.y}, {x: r.x + r.w, y: r.y},
            {x: r.x, y: r.y + r.h/2}, {x: r.x + r.w, y: r.y + r.h/2},
            {x: r.x, y: r.y + r.h}, {x: r.x + r.w/2, y: r.y + r.h}, {x: r.x + r.w, y: r.y + r.h}
        ];
        
        handles.forEach(h => {
            ctx.fillStyle = "#fff";
            ctx.strokeStyle = "#0078d7";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.rect(h.x - s/2, h.y - s/2, s, s);
            ctx.fill();
            ctx.stroke();
        });
        
        // 4. Instructional text
        if (r.w > 80 && r.h > 40) {
            ctx.fillStyle = "white";
            ctx.font = "bold 11px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.shadowColor = "black";
            ctx.shadowBlur = 4;
            ctx.fillText("ENTER ONAYLAR", r.x + r.w/2, r.y + r.h/2);
            ctx.fillText("ESC İPTAL", r.x + r.w/2, r.y + r.h/2 + 14);
        }

        ctx.restore();
    }
}


    // Draw Brush Preview Circle (follow mouse) for drawing tools
    // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] Removed !g.drawing gate so cursor always shows
    const drawingTools = [Tool.Pen.id, Tool.Brush.id, Tool.Eraser.id, Tool.Spray.id, 'btn-red-eye'];
    if (drawingTools.includes(g.current_tool.id) && !g.zooming && !g.movingSelection) {
        ctx.save();
        ctx.beginPath();
        // Use pen_width for radius (divided by 2)
        const radius = Math.max(2, g.pen_width / 2);
        ctx.arc(g.pX, g.pY, radius, 0, Math.PI * 2);
        
        ctx.globalAlpha = 0.8;
        
        // Outer black stroke for contrast
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#000';
        ctx.stroke();
        
        // Inner white stroke
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        
        ctx.restore();
    }

    // Also update originalCanvas to match the composite (for tools that read from it)
    if (!liveCanvas && originalCanvas) {
        const origCtx = originalCanvas.getContext("2d");
        if (origCtx) {
            origCtx.clearRect(0, 0, g.image_width, g.image_height);
            origCtx.drawImage(ctx.canvas, 0, 0);
        }
    }
}

/**
 * Get the active layer object
 */
export function getActiveLayer() {
    return layers[g.activeLayerIndex] || null;
}

// ─── Layer Panel UI ────────────────────────────────────────

/**
 * Update the Layers panel UI to reflect current layer state
 */
export function updateLayerPanel() {
    const layersList = document.querySelector('#layers-pane .layers-list');
    if (!layersList) return;

    layersList.innerHTML = '';

    // Render layers in reverse order (top layer first in the panel)
    for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers[i];
        const item = document.createElement('div');
        item.className = `layer-item${i === g.activeLayerIndex ? ' selected' : ''}`;
        item.dataset.index = i.toString();

        // Visibility toggle
        const visIcon = document.createElement('span');
        visIcon.className = `layer-icon${layer.visible ? ' active' : ''}`;
        visIcon.title = layer.visible ? 'Visible' : 'Hidden';
        visIcon.textContent = layer.visible ? '👁' : '·';
        visIcon.style.cursor = 'pointer';
        visIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleLayerVisibility(i);
        });

        // Thumbnail
        const thumb = document.createElement('div');
        thumb.className = 'layer-thumbnail';
        // Draw a small preview of the layer
        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = 32;
        thumbCanvas.height = 32;
        const thumbCtx = thumbCanvas.getContext('2d');
        if (thumbCtx) {
            thumbCtx.drawImage(layer.canvas as HTMLCanvasElement, 0, 0, g.image_width, g.image_height, 0, 0, 32, 32);
        }
        thumb.appendChild(thumbCanvas);

        // Name (editable on double-click)
        const nameDiv = document.createElement('div');
        nameDiv.className = 'layer-name';
        nameDiv.textContent = layer.name;
        nameDiv.addEventListener('dblclick', (e: MouseEvent) => {
            e.stopPropagation();
            const input = document.createElement('input');
            input.type = 'text';
            input.value = layer.name;
            input.className = 'layer-name-input';
            input.style.cssText = 'width: 100%; font-size: 11px; border: 1px solid #5b9bd5; padding: 1px 3px; background: #fff;';
            nameDiv.replaceWith(input);
            input.focus();
            input.select();
            const finish = () => {
                renameLayer(i, input.value || layer.name);
            };
            input.addEventListener('blur', finish);
            input.addEventListener('keydown', (ke: KeyboardEvent) => {
                if (ke.key === 'Enter') finish();
                if (ke.key === 'Escape') {
                    input.value = layer.name;
                    finish();
                }
            });
        });

        // Lock indicator
        const lockIcon = document.createElement('span');
        lockIcon.className = 'layer-icon';
        lockIcon.title = layer.locked ? 'Locked' : 'Unlocked';
        lockIcon.textContent = layer.locked ? '🔒' : '';
        lockIcon.style.cursor = 'pointer';
        lockIcon.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation();
            layers[i].locked = !layers[i].locked;
            updateLayerPanel();
        });

        // Blend Mode Dropdown
        const blendSelect = document.createElement('select');
        blendSelect.className = 'layer-blend-mode';
        blendSelect.style.cssText = 'font-size: 10px; margin-left: 5px; width: 60px;';

        const modes = [
            'source-over', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
            'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference',
            'exclusion', 'hue', 'saturation', 'color', 'luminosity'
        ];

        // Map internal names to display names if needed (e.g. source-over -> Normal)
        const displayNames: Record<string, string> = {
            'source-over': 'Normal',
            'color-dodge': 'Color Dodge',
            'color-burn': 'Color Burn',
            'hard-light': 'Hard Light',
            'soft-light': 'Soft Light'
        };

        modes.forEach(mode => {
            const option = document.createElement('option');
            option.value = mode;
            option.textContent = displayNames[mode] || mode.charAt(0).toUpperCase() + mode.slice(1);
            if (layer.blendMode === mode) option.selected = true;
            blendSelect.appendChild(option);
        });

        const stopProp = (e: Event) => e.stopPropagation();
        blendSelect.addEventListener('mousedown', (e: MouseEvent) => {
            e.stopPropagation();
            // Capture initial value for history
            (blendSelect as any).dataset.startValue = layers[i].blendMode;
        });
        blendSelect.addEventListener('mouseup', stopProp);
        blendSelect.addEventListener('click', stopProp);

        // Handle change
        blendSelect.addEventListener('change', (e: Event) => {
            e.stopPropagation();
            const newValue = (e.target as HTMLSelectElement).value;
            const startValue = (blendSelect as any).dataset.startValue || layers[i].blendMode;

            if (window.historyManager && startValue !== newValue) {
                window.historyManager.push(new LayerPropertyAction(i, 'blendMode', startValue as any, newValue as any));
            }

            layers[i].blendMode = newValue as any;
            renderLayers();
        });


        // Click to select layer
        item.addEventListener('click', () => {
            selectLayer(i);
        });

        item.appendChild(visIcon);
        item.appendChild(thumb);
        item.appendChild(nameDiv);
        item.appendChild(blendSelect); // Add dropdown
        item.appendChild(lockIcon);
        layersList.appendChild(item);
    }

    // Update layer opacity slider if it exists
    const layerOpacitySlider = document.getElementById('layerOpacity') as HTMLInputElement;
    if (layerOpacitySlider) {
        layerOpacitySlider.value = Math.round(layers[g.activeLayerIndex].opacity * 100).toString();
    }
}

// ─── Initialization ────────────────────────────────────────

document.addEventListener('DOMContentLoaded', function () {
    // Wire up layer panel buttons
    const addLayerBtn = document.querySelector('.panel-footer-btn[title="Add Layer"]');
    const deleteLayerBtn = document.querySelector('.panel-footer-btn[title="Delete Layer"]');
    const moveUpBtn = document.querySelector('.panel-footer-btn[title="Move Up"]');
    const moveDownBtn = document.querySelector('.panel-footer-btn[title="Move Down"]');

    if (addLayerBtn) {
        addLayerBtn.addEventListener('click', () => addLayer());
    }
    if (deleteLayerBtn) {
        deleteLayerBtn.addEventListener('click', () => deleteLayer());
    }
    if (moveUpBtn) {
        moveUpBtn.addEventListener('click', () => moveLayerUp());
    }
    if (moveDownBtn) {
        moveDownBtn.addEventListener('click', () => moveLayerDown());
    }

    // Layer Opacity Slider
    const layerOpacitySlider = document.getElementById('layerOpacity') as HTMLInputElement;
    if (layerOpacitySlider) {
        layerOpacitySlider.addEventListener('input', (e: Event) => {
            const val = parseInt((e.target as HTMLInputElement).value);
            const layer = layers[g.activeLayerIndex];
            if (layer) {
                layer.opacity = val / 100;
                renderLayers();
            }
        });

        let startOpacity = 1.0;
        layerOpacitySlider.addEventListener('mousedown', () => {
            const layer = layers[g.activeLayerIndex];
            if (layer) startOpacity = layer.opacity;
        });

        layerOpacitySlider.addEventListener('change', () => {
            const layer = layers[g.activeLayerIndex];
            if (layer && window.historyManager) {
                window.historyManager.push(new LayerPropertyAction(g.activeLayerIndex, 'opacity', startOpacity, layer.opacity));
            }
        });
    }

    // Initial render of the layer panel
    setTimeout(() => {
        updateLayerPanel();
    }, 100);

    console.log('Layers manager initialized');
});

import { appEvents } from '@hcie/core';

// Listen for document switch events from the core to update the UI
appEvents.addEventListener('document:switch', () => {
    renderLayers();
    updateLayerPanel();
});

// ─── Event Bus Listeners ───────────────────────────────────

EventBus.on('RENDER_LAYERS', () => renderLayers());
EventBus.on('UPDATE_LAYER_PANEL', () => updateLayerPanel());

EventBus.on('ADD_LAYER', (payload) => {
    addLayer(payload.name);
});

EventBus.on('SELECT_LAYER', (payload) => {
    selectLayer(payload.index);
});

EventBus.on('RENDER_TEXT_LAYER', (payload) => {
    renderTextLayer(payload.layer);
});


export function renderVectorLayer(layer: LayerClass) {
    if (!layer) return;

    if (layer.type === 'vector') {
        const ctx: any = layer.ctx;
        ctx.clearRect(0, 0, (layer.canvas as any).width, (layer.canvas as any).height);
        drawShapesToCtx(ctx, layer.shapes || []);
    }
}

export function renderTextLayer(layer: LayerClass) {
    if (!layer || layer.type !== 'text') return;

    const ctx: any = layer.ctx;
    const data = layer.textData;

    ctx.clearRect(0, 0, (layer.canvas as any).width, (layer.canvas as any).height);

    if (layer.isBeingEdited) return; // Hide text while editing to avoid ghosting


    ctx.save();
    const style = data.italic ? 'italic' : 'normal';
    const weight = data.bold ? 'bold' : 'normal';
    ctx.font = `${style} ${weight} ${data.size}px ${data.font}, "Roboto", sans-serif`;
    ctx.fillStyle = data.color;
    ctx.textBaseline = 'top';

    const lines = (data.text || "").split('\n');
    const lineHeight = data.size * 1.2;

    lines.forEach((line: string, index: number) => {
        ctx.fillText(line, data.x, data.y + (index * lineHeight));
    });
    ctx.restore();
}
